import React, { useCallback, useRef, useState } from "react";
import AudioFileOutlinedIcon from "@mui/icons-material/AudioFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { Box, Button, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { AUDIO_UPLOAD_MAX_BYTES, formatAudioBytes, getAudioUploadValidationError } from "./audioUtils";

const ACCEPT =
    ".mp3,.wav,.caf,audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/caf,audio/x-caf,audio/x-coreaudio";

export default function AudioFileDropZone({
    file = null,
    onFileChange,
    disabled = false,
    required = false,
}) {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [rejectMessage, setRejectMessage] = useState(null);

    const applyFile = useCallback(
        (next) => {
            if (!next) {
                setRejectMessage(null);
                onFileChange(null);
                return;
            }
            const err = getAudioUploadValidationError(next);
            if (err) {
                setRejectMessage(err);
                onFileChange(null);
                return;
            }
            setRejectMessage(null);
            onFileChange(next);
        },
        [onFileChange]
    );

    const onDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setDragOver(true);
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setDragOver(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDragOver(false);
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (disabled) return;
        const dropped = e.dataTransfer?.files?.[0];
        if (!dropped) return;
        applyFile(dropped);
    };

    const ready = Boolean(file) && !getAudioUploadValidationError(file);
    const borderColor = rejectMessage
        ? "error.main"
        : ready
          ? "success.main"
          : dragOver
            ? "primary.main"
            : "divider";
    const bgcolor = rejectMessage
        ? "rgba(211, 47, 47, 0.08)"
        : ready
          ? "rgba(46, 125, 50, 0.1)"
          : dragOver
            ? "action.hover"
            : "background.paper";

    return (
        <Box>
            <Box
                onDragEnter={onDragEnter}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onClick={() => !disabled && inputRef.current?.click()}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => {
                    if (disabled) return;
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        inputRef.current?.click();
                    }
                }}
                sx={{
                    border: "2px dashed",
                    borderColor,
                    borderRadius: 2,
                    bgcolor,
                    py: 3,
                    px: 2,
                    textAlign: "center",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.6 : 1,
                    transition: "border-color 0.15s, background-color 0.15s",
                    "&:hover": disabled
                        ? {}
                        : {
                              borderColor: ready ? "success.main" : "primary.main",
                              bgcolor: ready ? "rgba(46, 125, 50, 0.1)" : "action.hover",
                          },
                }}
            >
                {ready ? (
                    <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
                ) : (
                    <CloudUploadOutlinedIcon
                        sx={{ fontSize: 40, color: dragOver ? "primary.main" : "text.secondary", mb: 1 }}
                    />
                )}

                {ready ? (
                    <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "success.dark" }}>
                            Ready to upload
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mt: 0.5 }}>
                            <AudioFileOutlinedIcon fontSize="small" color="action" />
                            <Typography variant="body2">{file.name}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {formatAudioBytes(file.size)} · MP3, WAV, or CAF · max{" "}
                            {formatAudioBytes(AUDIO_UPLOAD_MAX_BYTES)}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {dragOver ? "Drop file here" : "Drag and drop an audio file"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            or click to browse
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            MP3, WAV, or CAF · max {formatAudioBytes(AUDIO_UPLOAD_MAX_BYTES)}
                            {required ? " · required" : ""}
                        </Typography>
                    </>
                )}
            </Box>

            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                hidden
                disabled={disabled}
                onChange={(e) => {
                    applyFile(e.target.files?.[0] ?? null);
                    e.target.value = "";
                }}
            />

            {rejectMessage ? (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {rejectMessage}
                </Typography>
            ) : null}

            {file && !rejectMessage ? (
                <Button
                    size="small"
                    sx={{ mt: 1 }}
                    disabled={disabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        applyFile(null);
                        if (inputRef.current) inputRef.current.value = "";
                    }}
                >
                    Clear file
                </Button>
            ) : null}
        </Box>
    );
}

AudioFileDropZone.propTypes = {
    file: PropTypes.object,
    onFileChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
};
